import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchLatestBidEventByReferenceNumber } from "../../api/bid";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import PageLoading from "../../components/PageLoading";
import UnauthorizedMessage from "../../components/UnauthorizedMessage";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { logout } from "../../util/authentication";
import { BID_TYPE } from "../../util/bid/bidConstants";
import { getScrubbedBidEvent } from "../../util/bid/bidScrubber";
import { getEidFromHref, getReferenceNumberFromHref, isAssignmentUsedToDisplay } from "../../util/bid/shiftBidUtils.js";
import { NO_ACCESS_TO_PAGE_MESSAGE } from "../../util/messageConstants";
import FreedayBidChoices from "./FreedayBidChoices";
import ShiftBidChoices from "./ShiftBidChoices";
import IAMFTOBidChoices from "./IAMFTOBidChoices";
import TWUDATFTOBidChoices from "./TWUDATFTOBidChoices";
import IAMDATBidChoices from "./IAMDATBidChoices";
import NonContractPTOFTOBidChoices from "./NonContractPTOFTOBidChoices";
import NonContractPTOGOCHOLBidChoices from "./NonContractPTOGOCHOLBidChoices";

const BidChoicesPage = (props) => {
  const userSession = useSelector(selectUserSession);

  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [hasAccessToViewPage, setHasAccessToViewPage] = useState(false);
  const [bidData, setBidData] = useState({});

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      const eid = getEidFromHref();
      if (eid !== userSession.eid) {
        setHasAccessToViewPage(false);
        setIsPageLoading(false);
      } else {
        console.log("Retrieving bid data...");
        loadBidMetaData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const loadBidMetaData = () => {
    setIsPageLoading(true);
    const referenceNumber = getReferenceNumberFromHref();
    fetchLatestBidEventByReferenceNumber(userSession.eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response.event) {
          const bidEvent = getScrubbedBidEvent(response.event);
          setBidData(bidEvent);
          setIsPageLoading(false);
          setHasAccessToViewPage(true);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
        setIsPageLoading(false);
      });
  };

  const renderBidChoicesPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    } else if (!hasAccessToViewPage) {
      return <div className="page-error">{NO_ACCESS_TO_PAGE_MESSAGE}</div>;
    }

    if (bidData.bidType === BID_TYPE.SHIFT) {
      return (
        <ShiftBidChoices
          {...props}
          featureConfig={props.featureConfig}
          isDesktopView={props.isDesktopView}
          useAssignmentForEmp={isAssignmentUsedToDisplay(bidData)}
        />
      );
    }

    if (bidData.bidType === BID_TYPE.FREEDAY) {
      return <FreedayBidChoices {...props} featureConfig={props.featureConfig} isDesktopView={props.isDesktopView} />;
    }

    if (bidData.bidType === BID_TYPE.FTO) {
      return <IAMFTOBidChoices {...props} featureConfig={props.featureConfig} isDesktopView={props.isDesktopView} />;
    }

    if (bidData.bidType === BID_TYPE.TWU_DAT_FTO) {
      return <TWUDATFTOBidChoices {...props} featureConfig={props.featureConfig} isDesktopView={props.isDesktopView} />;
    }

    if (bidData.bidType === BID_TYPE.IAM_DAT) {
      return <IAMDATBidChoices {...props} featureConfig={props.featureConfig} isDesktopView={props.isDesktopView} />;
    }

    if (bidData.bidType === BID_TYPE.NC_PTO_FTO) {
      return (
        <NonContractPTOFTOBidChoices
          {...props}
          featureConfig={props.featureConfig}
          isDesktopView={props.isDesktopView}
        />
      );
    }

    if (bidData.bidType === BID_TYPE.NC_PTO_GOC_HOL) {
      return (
        <NonContractPTOGOCHOLBidChoices
          {...props}
          featureConfig={props.featureConfig}
          isDesktopView={props.isDesktopView}
        />
      );
    }

    return <></>;
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div data-testid="manage-bid-choices-page">
      {isUserAuthorizedStateLoading ? <PageLoading /> : renderBidChoicesPage()}
    </div>
  );
};

BidChoicesPage.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
};

export default BidChoicesPage;
